import { Button, Text, VStack, Wrap, WrapItem } from '@chakra-ui/react';
import type { PersonalizedPromptDescriptor } from '@bower/shared';
import { MaterialIcon } from '../ui/MaterialIcon';
import { StatusBanner } from '../ui/StatusBanner';
import { useSuggestedPromptAnalytics } from '../../hooks/analytics/useSuggestedPromptAnalytics';
import type { PersonalizedPromptAnalyticsSource } from '../../utils/personalizedPromptSelection';

interface SuggestedPromptsProps {
  readonly prompts: readonly PersonalizedPromptDescriptor[];
  readonly source: PersonalizedPromptAnalyticsSource;
  readonly onSelect: (prompt: PersonalizedPromptDescriptor) => void;
  readonly direction?: 'row' | 'column';
  readonly isError?: boolean;
  readonly isRetrying?: boolean;
  readonly onRetry?: () => void;
}

/**
 * Evidence-aware prompt chips shared by the dashboard and Bower empty state.
 * Analytics carries only stable pool metadata; prompt text and source labels
 * can contain user-derived titles and must not be recorded.
 */
export function SuggestedPrompts({
  prompts,
  source,
  onSelect,
  direction = 'column',
  isError = false,
  isRetrying = false,
  onRetry,
}: SuggestedPromptsProps) {
  const visiblePrompts = isError ? [] : prompts;
  const trackSelection = useSuggestedPromptAnalytics(visiblePrompts, source);

  const handleClick = (prompt: PersonalizedPromptDescriptor, position: number) => {
    trackSelection(prompt, position);
    onSelect(prompt);
  };

  if (isError) {
    return (
      <StatusBanner
        tone="warning"
        title="Suggested prompts are unavailable"
        description="You can still ask Bower a question, or try loading suggestions again."
        action={
          onRetry ? { label: 'Try again', onClick: onRetry, isLoading: isRetrying } : undefined
        }
      />
    );
  }

  // Render nothing rather than an empty list container. A successful empty
  // pool is meaningful and distinct from the retryable error above.
  if (visiblePrompts.length === 0) return null;

  const chips = visiblePrompts.map((prompt, i) => {
    const button = (
      <Button
        key={prompt.key}
        variant="outline"
        size="sm"
        w={direction === 'column' ? 'full' : undefined}
        justifyContent="flex-start"
        textAlign="left"
        // A PILL (2026-09): parchment fill, hairline, pill radius, twilight
        // glyph. `bg.surface` is that parchment since the card inversion.
        //
        // Text still WRAPS, and the pill is `h="auto"` rather than a fixed
        // single line, because real prompts are not the short ones the design
        // sheet draws — a personalized one runs to a full sentence naming a
        // note by title, and a single-line pill would ellipsise the half that
        // says what it does.
        whiteSpace="normal"
        h="auto"
        // 44px is the AAA touch target; 2.5.8 only needs 24. Kept because these
        // are a primary way in on a tablet at the bench.
        minH="44px"
        py={2}
        px={4}
        borderRadius="full"
        bg="bg.surface"
        borderColor="border.default"
        color="text.primary"
        fontSize="sm"
        fontWeight="normal"
        leftIcon={<MaterialIcon name="auto_awesome" boxSize={4} gradient="warm" />}
        _hover={{ bg: 'bg.hover', borderColor: 'border.hover' }}
        onClick={() => handleClick(prompt, i)}
      >
        <Text as="span">{prompt.text}</Text>
      </Button>
    );
    // Only the row layout wraps each chip in a WrapItem (<li>) — and only there
    // is it nested in a <Wrap> (<ul>). The column layout renders buttons
    // directly in a <VStack>; wrapping them in WrapItem would emit orphan <li>
    // elements with no <ul>/<ol> parent (a WCAG 1.3.1 / axe `listitem`
    // violation on the Bower chat empty state).
    return direction === 'row' ? <WrapItem key={prompt.key}>{button}</WrapItem> : button;
  });

  if (direction === 'row') {
    return <Wrap spacing={2}>{chips}</Wrap>;
  }
  return (
    <VStack spacing={2} align="stretch" w="full">
      {chips}
    </VStack>
  );
}
